<template>
  <div :class="ns.b()">
    <template v-for="(c, i) in innerValue" :key="i">
      <LtInput
        :ref="(el: any) => (inputRefList[i] = el)"
        :type="type"
        :class="inputCls"
        :model-value="c"
        :size="size"
        :error="error"
        :disabled="disabled"
        :readonly="readonly"
        @focus="() => focusFirstEmptyInput(i)"
        @input="(v) => handleInput(i, v)"
        @keydown="(e) => handleKeydown(i, e)"
        @paste="(e: ClipboardEvent) => handlePaste(e, i)"
      />
      {{ separator?.(i, c) }}
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useNamespace, useSizeProp } from '@lit-element/hooks'
import LtInput from '@lit-element/components/input'
import { isFunction, isString } from '@lit-element/utils'
import { ArrowLeft, ArrowRight, Backspace } from './keycode'
import type { PropType, VNode } from 'vue'

defineOptions({ name: 'LtVerificationCode' })

const props = defineProps({
  modelValue: String, // 绑定值
  defaultValue: String, // 默认值（非受控状态）
  // 验证码的长度，根据长度渲染对应个数的输入框
  length: {
    type: Number,
    default: 6,
  },
  /**
   * @zh 输入框大小
   * @values 'mini','small','medium','large'
   * @defaultValue 'medium'
   */
  size: useSizeProp,
  disabled: Boolean, // 是否禁用
  masked: Boolean, // 是否密码模式
  readonly: Boolean, //只读
  //是否为错误状态
  error: {
    type: Boolean,
    default: false,
  },
  // 分隔符。可在不同索引的输入框后自定义渲染分隔符
  separator: {
    type: Function as PropType<(index: number, character: string) => VNode>,
  },
  // 格式化函数，当用户输入值改变时触发
  formatter: {
    type: Function as PropType<
      (inputValue: string, index: number, value: string) => string | boolean
    >,
  },
})

const emits = defineEmits(['update:modelValue', 'change', 'finish', 'input'])

const ns = useNamespace('verification-code')
const ns2 = useNamespace('input')

const inputRefList = ref([] as HTMLElement[])

const mergedValue = computed(() => props.modelValue ?? props.defaultValue)
const type = computed(() => (props.masked ? 'password' : 'text'))
const inputCls = computed(() => [
  ns2.b(),
  {
    [ns2.b(`size-${props.size}`)]: props.size,
  },
])

const filledValue = computed(() => {
  const newVal = String(mergedValue.value).split('')
  return Array.from({ length: props.length })
    .fill('')
    .map((_, index) => (newVal[index] ? String(newVal[index]) : '')) as string[]
})

const innerValue = ref(filledValue.value)

watch(mergedValue, () => {
  innerValue.value = filledValue.value
})

const updateValue = () => {
  const value = innerValue.value.join('').trim()
  emits('update:modelValue', value)
  emits('change', value)
  if (value.length === props.length) {
    emits('finish', value)
  }
  focusFirstEmptyInput()
}

const handleFocus = (index: number) => inputRefList?.value[index].focus()
const focusFirstEmptyInput = (index?: number) => {
  if ((index || index === 0) && innerValue.value[index as number]) {
    return
  }
  for (let i = 0; i < innerValue.value.length; i++) {
    if (!innerValue.value[i]) {
      handleFocus(i)
      break
    }
  }
}

const handlePaste = (e: ClipboardEvent, index: number) => {
  e.preventDefault()
  const { clipboardData } = e
  const text = clipboardData?.getData('text')
  if (!text) return

  text.split('').forEach((char, i) => {
    if (index + i >= props.length) return

    if (isFunction(props.formatter)) {
      const result = props.formatter(char, index + i, innerValue.value.join(''))
      if (result === false) {
        index -= 1
        return
      }
      if (isString(result)) {
        char = result.charAt(0)
      }
    }

    innerValue.value[index + i] = char
  })
  updateValue()
}

const handleKeydown = (index: number, e: any) => {
  const keyCode = e.code || e.key

  if (keyCode === Backspace.code && !innerValue.value[index]) {
    e.preventDefault()
    innerValue.value[Math.max(index - 1, 0)] = ''
    updateValue()
  } else if (keyCode === ArrowLeft.code && index > 0) {
    e.preventDefault()
    handleFocus(index - 1)
  } else if (
    keyCode === ArrowRight.code &&
    innerValue.value[index] &&
    index < props.length - 1
  ) {
    e.preventDefault()
    handleFocus(index + 1)
  }
}

const handleInput = (index: number, value: string) => {
  let char = (value || '').trim().charAt(value.length - 1)
  emits('input', char, index)

  if (isFunction(props.formatter)) {
    const result = props.formatter(char, index, innerValue.value.join(''))
    if (result === false) return
    if (isString(result)) {
      char = result.charAt(0)
    }
  }

  innerValue.value[index] = char
  updateValue()
}
</script>
